// Reading the map out of a project document: place cards as pins (and how to colour them), routes,
// and which cards are linked to a place. Pure functions, used by the browser and the agent tools.
import * as Y from 'yjs';
import { cardsMap, parseCard, parseRoute, routesMap, type Card, type Route } from './doc.js';
import { effectiveTrayState } from './ops.js';

export type Place = Extract<Card, { kind: 'place' }>;
export type PinStatus = 'question' | 'researched' | 'confirmed' | 'none';

/** Pin colours by the status of the findings linked to a place: what needs attention stands out. */
export const PIN_COLOURS: Record<PinStatus, string> = { question: '#dc2626', researched: '#ca8a04', confirmed: '#16a34a', none: '#64748b' };
const RANK: Record<PinStatus, number> = { none: 0, confirmed: 1, researched: 2, question: 3 };

/** Every readable place card, by name. Invalid ones (e.g. a latitude of 91) are left off the map. */
export function listPlaces(doc: Y.Doc): Place[] {
  const out: Place[] = [];
  cardsMap(doc).forEach((y) => {
    if (!(y instanceof Y.Map) || y.get('kind') !== 'place') return;
    const card = parseCard(y.toJSON());
    if (card?.kind === 'place') out.push(card);
  });
  return out.sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
}

/** Every readable route, oldest first. */
export function listRoutes(doc: Y.Doc): Route[] {
  const out: Route[] = [];
  routesMap(doc).forEach((y) => {
    const r = y instanceof Y.Map ? parseRoute(y.toJSON()) : null;
    if (r) out.push(r);
  });
  return out.sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
}

/**
 * Each place's pin status from the findings linked to it (their placeCardId), ignoring dismissed
 * ones. What needs attention wins: any open question makes it "question"; otherwise any unverified
 * finding makes it "researched"; otherwise "confirmed". Places with no such findings aren't in the
 * map (read them as "none").
 */
export function placeStatuses(doc: Y.Doc): Map<string, PinStatus> {
  const out = new Map<string, PinStatus>();
  cardsMap(doc).forEach((y, id) => {
    if (!(y instanceof Y.Map) || y.get('kind') !== 'finding') return;
    const placeId = y.get('placeCardId');
    const status = y.get('status');
    if (typeof placeId !== 'string' || (status !== 'question' && status !== 'researched' && status !== 'confirmed')) return;
    if (effectiveTrayState(doc, id) === 'dismissed') return;
    if (RANK[status as PinStatus] > RANK[out.get(placeId) ?? 'none']) out.set(placeId, status);
  });
  return out;
}

/** Cards linked to a place (their placeCardId), oldest first. */
export function cardsAtPlace(doc: Y.Doc, placeId: string): string[] {
  const found: { id: string; at: string }[] = [];
  cardsMap(doc).forEach((y, id) => {
    if (id !== placeId && y instanceof Y.Map && y.get('placeCardId') === placeId) found.push({ id, at: String(y.get('createdAt') ?? '') });
  });
  return found.sort((a, b) => a.at.localeCompare(b.at) || a.id.localeCompare(b.id)).map((f) => f.id);
}

/** A short plain-text label for a card in a list. */
export function cardLabel(card: Card): string {
  const clip = (s: string) => (s.length > 80 ? `${s.slice(0, 79)}…` : s);
  switch (card.kind) {
    case 'note':
      // A note's body is its XML (Y.XmlFragment.toJSON()): keep the text.
      return clip(card.body.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()) || 'Empty note';
    case 'image':
      return clip(card.caption || card.credit || 'Image');
    case 'link':
      return clip(card.title || card.url);
    case 'palette':
      return `Palette (${card.colours.length} colours)`;
    case 'todo':
      return clip(card.title || 'To-do list');
    case 'finding':
      return clip(card.claim);
    case 'place':
      return clip(card.name || 'Untitled place');
  }
}

const TAG_COLOURS = ['#2563eb', '#db2777', '#0891b2', '#7c3aed', '#d97706', '#4d7c0f', '#e11d48', '#0f766e'];

/** A stable colour per tag ("colour pins by tag"); grey for a place with no tags. */
export function tagColour(tag: string | undefined): string {
  if (!tag) return PIN_COLOURS.none;
  let h = 0;
  for (const c of tag) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return TAG_COLOURS[h % TAG_COLOURS.length];
}
